import { conditionalRender } from '@repo/toolkit/conditional-render'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { CalendarTwoIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createCenterEvent } from '../../../api/center-event/create-center-event'
import { Paths } from '../../../lib/constants/path'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { EventStatusEnum } from '../../../lib/types/enums/center-event/event-status.enum'
import {
  CenterEvent,
  CenterEventFilter
} from '../../../lib/types/models/center-event/center-event'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformCenterEventsFiltersToUrl,
  transformUrlToCenterEventsFilters
} from '../../../lib/utils/filters-mapping/center-events'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { replaceParams } from '../../../views/centers/utils'
import FilterDrawer from '../../elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef, HeaderType } from '../../elements/dynamic-table'
import { countFilters } from '../../elements/dynamic-table/utils/countFilters'
import CenterEventForm from './center-event-form'
import { CenterEventsFilter } from './center-events-filter'
import { CenterEventVersion, getCenterEventColumns } from './utils'

type Props = {
  centerId?: number
  headerType?: HeaderType
  version: CenterEventVersion
}

export function CenterEventTable({ centerId, version, headerType }: Props) {
  const { navigateWithEvent } = useNavigator()
  const { t } = useTranslate()
  const tableRef = useRef<DynamicTableRef<CenterEvent>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<CenterEventFilter>({
      initialFilters: {
        ...(centerId && { centerIds: [centerId] }),
        statuses: [EventStatusEnum.CREATED, EventStatusEnum.IN_PROGRESS]
      },
      options: { allowSearchParam: false },
      transformFilterToUrl: transformCenterEventsFiltersToUrl,
      transformUrlToFilter: transformUrlToCenterEventsFilters
    })
  const { modalState, toggleModal, closeModel } = useModalManager<
    'filters' | 'create',
    CenterEvent[]
  >(['filters', 'create'])

  const { mutateAsync } = useMutation({
    mutationFn: createCenterEvent,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Evènement ajouté avec succès')
      closeModel('create')
    }
  })

  const filtersBasedOnMode = useMemo(() => {
    if (version === 'PARTIAL' && centerId) return { ...selectedFilter, centerIds: [centerId] }
    return selectedFilter
  }, [version, centerId, selectedFilter])

  return (
    <>
      <DynamicDataTable<CenterEvent, CenterEventFilter>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        headerType={headerType}
        title={version === 'FULL' ? 'Tous les évènements' : 'Evènements en séjour'}
        permissions={{ delete: false }}
        columns={getCenterEventColumns(version, t)}
        allowSearchParam
        endpoint={API.centerEvents.getAll.url}
        filters={filtersBasedOnMode}
        filtersLength={countFilters(filtersBasedOnMode)}
        paginated
        displayOrder
        onRowClick={(row, event) => {
          const center = version === 'PARTIAL' ? centerId : row.center.id
          if (center)
            navigateWithEvent(
              replaceParams(Paths.CENTER_EVENT_DETAILS_PAGE, { id: center, eventId: row.id }),
              event
            )
        }}
        onFiltersClick={() => toggleModal('filters')}
        actions={conditionalRender(version === 'PARTIAL', [
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Ajouter un nouvel évènement',
            fn: () => toggleModal('create')
          }
        ])}
      />
      {centerId && (
        <Modal
          isOpen={modalState.create}
          title={'Ajouter un évènement'}
          icon={CalendarTwoIcon}
          hasBorderBottom
          hideActions
          size="medium"
          onCancel={() => closeModel('create')}
        >
          <CenterEventForm
            centerId={centerId}
            buttonsConfig={modalButtonConfig({
              onConfirm(values) {
                return mutateAsync({ ...values, centerId })
              },
              onCancel: () => closeModel('create')
            })}
          />
        </Modal>
      )}

      {/* Filters Modal */}
      <FilterDrawer<CenterEventFilter>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <CenterEventsFilter filter={filter} onFilterChange={onFilterChange} centerId={centerId} />
      </FilterDrawer>
    </>
  )
}
